import './HeroKicker.css';
import type { ComponentChildren } from 'preact';
import { AnimatedNumber } from '../../components/AnimatedNumber';
import { StatusChip } from '../../components/StatusChip';
import { EDITORIAL, type EditorialChoice } from '../../copy/editorial';
import { t } from '../../copy/strings';
import { fmtSections } from '../../format/numbers';
import { fmtTime } from '../../format/time';
import { epochMs, tMinOfIso } from '../../model/time';
import type { Decision, ResultsView } from '../../model/types';
import { useConfig, useView } from '../../state/hooks';

/** Marcador de `{S}` no modelo do kicker: separa o texto ao redor do percentual animado. */
const SLOT = '\u0000';

/** Decisão exibida no chip: a do TSE prevalece sobre a matemática (§7.2 `HeroModel.decided`). */
export function heroDecision(view: ResultsView | null): Decision | null {
  if (view === null) return null;
  return view.national.tseResult ?? view.national.mathDecision;
}

/** ORD-F-149 item 1 (em aprovação): o selo da definição matemática de cada escolha da trava. */
const MATH_CHIP = {
  today: 'hero.chip.math',
  proposal: 'hero.chip.math.count',
  alternative: 'hero.chip.math.lead',
} as const;

/**
 * Texto do chip de definição (§7.3): "Definido matematicamente às {HHhMM}" ou "{Eleito|Eleita} · TSE · {HHhMM}", com a
 * hora de `decided.at` por `fmtTime` (depois da meia-noite, "seg., 00h51"; M15). Null sem decisão. O selo da definição
 * matemática segue a trava do item 1 da ORD-F-149 (`choice`; padrão: `EDITORIAL[1]`, a proposta aprovada, ORD-F-150).
 */
export function heroChipText(
  view: ResultsView,
  electionDate: string,
  choice: EditorialChoice = EDITORIAL[1],
): string | null {
  const d = heroDecision(view);
  if (d === null) return null;
  const hhmm = fmtTime(tMinOfIso(d.at, epochMs(electionDate)), electionDate);
  if (d.kind === 'math') return t(MATH_CHIP[choice], { HHhMM: hhmm });
  const winner = view.candidates.find((c) => c.number === d.winner);
  return t('hero.chip.tse', { 'X.gender': winner?.gender ?? null, HHhMM: hhmm });
}

export interface HeroKickerProps {
  /** Primeiro snapshot dentro da janela `intro` (§7.2 bloco 5): o percentual conta de 90% a 100%. */
  intro?: boolean;
  /**
   * Herói na variante `--pre` (§7.2 bloco 4: `connecting`, `countdown`, `opened_no_votes` ou sem votos): só
   * "Presidente · Brasil", sem seções e sem chip, qualquer que seja o payload.
   */
  pre?: boolean;
}

/**
 * Linha de contexto do herói (§7.3): "Presidente · Brasil" (sem snapshot, sem votos ou depois da definição) ou
 * "Presidente · Brasil · {S} das seções" (apurando, `{S}` animado e truncado por `fmtSections`), e o chip de definição
 * à direita, que quebra para a linha de baixo quando não cabe. Não interativo; não é cabeçalho. A TV não o renderiza
 * (o `HeroCard` decide, §7.2 bloco 6).
 */
export function HeroKicker({ intro = false, pre = false }: HeroKickerProps) {
  const view = useView();
  const electionDate = useConfig().election_date;
  const n = view?.national ?? null;
  const decided = pre ? null : heroDecision(view);
  const counting = !pre && n !== null && n.validVotes > 0 && decided === null;
  const chip = view !== null && decided !== null ? heroChipText(view, electionDate) : null;
  let text: ComponentChildren = t('hero.kicker.base');
  if (counting && n !== null) {
    const [before, after] = t('hero.kicker.counting', { S: SLOT }).split(SLOT) as [string, string];
    text = (
      <>
        {before}
        <AnimatedNumber value={n.sectionsFraction} format={fmtSections} intro={intro} class="hero-kicker__pct" />
        {after}
      </>
    );
  }
  const winner = decided ? view?.candidates.find((c) => c.number === decided.winner) : undefined;
  return (
    <p
      class="hero-kicker hero-card__kicker"
      data-testid="hero-kicker"
      data-state={decided ? decided.kind : counting ? 'counting' : 'base'}
    >
      <span class="hero-kicker__text">{text}</span>
      {chip !== null && n !== null ? (
        <span class="hero-kicker__chip" title={chip}>
          <StatusChip
            status={n.status}
            sectionsFraction={n.sectionsFraction}
            decision={decided}
            gender={winner?.gender ?? null}
            label={chip}
          />
        </span>
      ) : null}
    </p>
  );
}
